import React, { useState, useEffect } from 'react';
import { moduleApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  LayoutDashboard, Heart, Calendar, MessageSquare, FormInput,
  Database, DollarSign, Target, ShieldCheck, BarChart3, Cloud,
  Pill, Video, Ticket, Settings, Loader2, Lock, ToggleLeft, ToggleRight,
  Stethoscope, TestTube2, Package
} from 'lucide-react';

const MODULE_ICONS = {
  dashboard: LayoutDashboard, patients: Heart, appointments: Calendar,
  messages: MessageSquare, forms: FormInput, ehr: Database,
  encounters: Stethoscope, labs: TestTube2, medications: Package,
  claims: DollarSign, crm: Target, cqm: ShieldCheck,
  analytics: BarChart3, mychart: Cloud, prescriptions: Pill,
  telehealth: Video, support: Ticket, admin: Settings,
};

export default function ModuleManagementPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await moduleApi.list();
      setModules(res.data.modules || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleToggle = async (mod) => {
    if (mod.locked) { toast.error('This module cannot be disabled'); return; }
    setToggling(mod.key);
    try {
      await moduleApi.toggle(mod.key, !mod.enabled);
      toast.success(`${mod.name} ${!mod.enabled ? 'enabled' : 'disabled'}`);
      setModules(prev => prev.map(m => m.key === mod.key ? { ...m, enabled: !m.enabled } : m));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle module'); }
    finally { setToggling(null); }
  };

  return (
    <div className="space-y-6" data-testid="module-management-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Module Management</h1>
        <p className="text-[#64748B] mt-1">Enable or disable platform modules for your organization</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((mod) => {
            const Icon = MODULE_ICONS[mod.key] || Settings;
            const isToggling = toggling === mod.key;
            return (
              <Card key={mod.key} className={`careiq-card transition-all ${mod.enabled ? 'border-[#0E7490]/30' : 'opacity-60'}`} data-testid={`module-card-${mod.key}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${mod.enabled ? 'bg-[#0E7490]/10' : 'bg-[#F1F5F9]'}`}>
                        <Icon className={`w-5 h-5 ${mod.enabled ? 'text-[#0E7490]' : 'text-[#94A3B8]'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#0A2342] text-sm">{mod.name}</h3>
                          {mod.locked && <Lock className="w-3 h-3 text-[#94A3B8]" />}
                        </div>
                        <p className="text-xs text-[#64748B] mt-0.5">{mod.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleToggle(mod)}
                      disabled={mod.locked || isToggling}
                      className={`transition-colors ${mod.locked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                      data-testid={`toggle-module-${mod.key}`}
                    >
                      {isToggling ? (
                        <Loader2 className="w-6 h-6 text-[#0E7490] animate-spin" />
                      ) : mod.enabled ? (
                        <ToggleRight className="w-8 h-8 text-[#0E7490]" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-[#94A3B8]" />
                      )}
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
